import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';

const DEFAULT_MIN_CHARS = 2;
const DEFAULT_DEBOUNCE_MS = 300;
const EMPTY_REQUIRED_GROUPS = [];
const STRUCTURED_DEBOUNCE_MS = 500;
const DATE_SEARCH_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'MMDDYYYY',
];

const clearArrayState = (setState) => {
  setState((current) => (current.length > 0 ? [] : current));
};

const normalizeDateSearchValue = (value) => {
  if (!value) return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : null;
  }

  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const parsed = moment(trimmed, DATE_SEARCH_FORMATS, true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const buildErrorMessage = (error) => {
  if (!error) return null;

  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  return 'Unable to search patients right now.';
};

const useEmrPatientSearch = ({
  credentialId,
  enabled,
  searchFields,
  minQueryLength = DEFAULT_MIN_CHARS,
  debounceMs,
  limit = 10,
  requiredGroups = EMPTY_REQUIRED_GROUPS,
  location,
  initialValues = {},
  initialValuesReady = false,
  searchTrigger = 'automatic',
  autoSearchInitialValues = false,
}) => {
  const [fieldValues, setFieldValues] = useState({});
  // Explicit-trigger schemas (CareCloud) search only the snapshot the user
  // submitted, so edits never fire a request on their own.
  const [submittedFieldValues, setSubmittedFieldValues] = useState(null);
  const [searchRequestId, setSearchRequestId] = useState(0);
  const [results, setResults] = useState([]);
  const [searchWarnings, setSearchWarnings] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [acceptedResponseSequence, setAcceptedResponseSequence] = useState(0);
  const [requestId, setRequestId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const cancelRef = useRef({ cancelled: false });
  const fieldValuesRef = useRef({});
  const isExplicitSearch = searchTrigger === 'explicit';

  // Determine mode
  const isStructuredSearch = searchFields?.length > 0
    && !searchFields.every((f) => f.key === 'query');

  // Use longer debounce for structured mode to allow tabbing between fields
  const effectiveDebounce = debounceMs
    ?? (isStructuredSearch ? STRUCTURED_DEBOUNCE_MS : DEFAULT_DEBOUNCE_MS);

  // Single-field updater (stable reference via useCallback)
  const setFieldValue = useCallback((key, value) => {
    if (fieldValuesRef.current[key] === value) return;

    cancelRef.current.cancelled = true;
    const nextFieldValues = { ...fieldValuesRef.current, [key]: value };
    fieldValuesRef.current = nextFieldValues;
    setFieldValues(nextFieldValues);
    // A rendered candidate belongs to the prior criteria. Clear immediately,
    // before the debounce begins, so it cannot be selected while stale.
    clearArrayState(setResults);
    clearArrayState(setSearchWarnings);
    setHasMore(false);
    setRequestId(null);
    setSubmittedFieldValues(null);
  }, []);

  const reset = useCallback(() => {
    cancelRef.current.cancelled = true;
    fieldValuesRef.current = {};
    setFieldValues({});
    setSubmittedFieldValues(null);
    setSearchRequestId(0);
    setResults([]);
    setSearchWarnings([]);
    setHasMore(false);
    setRequestId(null);
    setAcceptedResponseSequence(0);
    setIsLoading(false);
    setError(null);
  }, []);

  const initialValuesKey = useMemo(() => JSON.stringify(initialValues || {}), [initialValues]);

  // Reset on credential/location changes and apply prefill only after the
  // drawer has resolved all mapped values (including intentional blanks).
  useEffect(() => {
    cancelRef.current.cancelled = true;
    const nextFieldValues = initialValuesReady ? { ...initialValues } : {};
    fieldValuesRef.current = nextFieldValues;
    setFieldValues(nextFieldValues);
    // An explicit-trigger schema may still run one open-time lookup from the
    // record's demographics, matching the automatic schemas' prefill search.
    const seedExplicitSearch = isExplicitSearch
      && autoSearchInitialValues
      && Object.keys(nextFieldValues).length > 0;
    setSubmittedFieldValues(seedExplicitSearch ? nextFieldValues : null);
    setSearchRequestId(seedExplicitSearch ? (requestSequence) => requestSequence + 1 : 0);
    setResults([]);
    setSearchWarnings([]);
    setHasMore(false);
    setRequestId(null);
    setAcceptedResponseSequence(0);
    setIsLoading(false);
    setError(null);
  }, [credentialId, enabled, location, initialValuesReady, initialValuesKey, isExplicitSearch, autoSearchInitialValues]);

  // Compute whether we have enough input to trigger search.
  // Date fields are presence-checked (not length-checked).
  const canSearchValues = useCallback((values) => {
    if (!enabled || !credentialId) return false;

    if (!searchFields?.length) {
      // Legacy mode: check query length
      return (values.query || '').trim().length >= minQueryLength;
    }

    // Name fields are validated as a combined string to match the backend's
    // resolve_query_from_params, which joins them before length-checking.
    const nameKeys = ['first_name', 'last_name'];

    // Check that all required fields are satisfied
    const requiredFields = searchFields.filter((f) => f.required);
    const hasAllRequired = requiredFields.every((f) => {
      const val = values[f.key];
      if (f.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      // For name fields, require presence but defer length to the combined check
      if (nameKeys.includes(f.key)) return (val || '').trim().length > 0;
      return (val || '').trim().length >= minQueryLength;
    });

    if (!hasAllRequired) return false;

    const satisfiesField = (field) => {
      const val = values[field.key];
      if (field.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      if (nameKeys.includes(field.key)) return (val || '').trim().length > 0;
      return (val || '').trim().length >= minQueryLength;
    };

    const combinedName = nameKeys
      .map((k) => (values[k] || '').trim())
      .filter(Boolean)
      .join(' ');

    if (requiredGroups.length > 0) {
      const fieldsByKey = searchFields.reduce((memo, field) => ({
        ...memo,
        [field.key]: field,
      }), {});

      return requiredGroups.some((group) => {
        const complete = group.every((key) => {
          const field = fieldsByKey[key];
          return field && satisfiesField(field);
        });
        if (!complete) return false;

        // A group anchored by either name still obeys the backend's combined
        // minimum. This closes the old one-character + DOB fall-through while
        // preserving independent MRN/DOB groups used by other integrations.
        return group.some((key) => nameKeys.includes(key))
          ? combinedName.length >= minQueryLength
          : true;
      });
    }

    // For name fields that the backend joins, check the *combined* length
    // so canSearch and resolve_query_from_params agree on what's submittable.
    const hasValidName = combinedName.length >= minQueryLength;

    // At least one field must have meaningful input
    const hasAnyInput = searchFields.some((f) => {
      if (nameKeys.includes(f.key)) return hasValidName;
      const val = values[f.key];
      if (f.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      return (val || '').trim().length >= minQueryLength;
    });

    return hasAnyInput;
  }, [searchFields, minQueryLength, enabled, credentialId, requiredGroups]);

  const canSearch = useMemo(() => canSearchValues(fieldValues), [canSearchValues, fieldValues]);

  const search = useCallback(() => {
    if (!isExplicitSearch || !canSearchValues(fieldValuesRef.current)) return;

    setSubmittedFieldValues({ ...fieldValuesRef.current });
    setSearchRequestId((requestSequence) => requestSequence + 1);
  }, [canSearchValues, isExplicitSearch]);

  // Automatic schemas search the live criteria; explicit schemas search the
  // submitted snapshot (or nothing until the user submits one).
  const requestFieldValues = isExplicitSearch ? submittedFieldValues : fieldValues;
  const requestCanSearch = Boolean(requestFieldValues) && canSearchValues(requestFieldValues);

  // Stable serialization of the request criteria for useEffect dependency.
  const requestFieldValuesKey = useMemo(
    () => JSON.stringify(requestFieldValues),
    [requestFieldValues],
  );

  // Debounced search effect
  useEffect(() => {
    if (!requestCanSearch) {
      clearArrayState(setResults);
      clearArrayState(setSearchWarnings);
      setHasMore(false);
      setRequestId(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    setError(null);

    // Create cancellation token before the timer so stale requests
    // that resolve between effect runs are always discarded (#2).
    const cancellationToken = { cancelled: false };
    cancelRef.current = cancellationToken;

    const timerId = setTimeout(() => {
      if (cancellationToken.cancelled) return;

      setIsLoading(true);

      // Build request params based on mode
      let requestParams;
      if (isStructuredSearch) {
        const searchParams = {};
        searchFields.forEach((f) => {
          const val = requestFieldValues[f.key];
          if (f.component === 'date') {
            const normalizedDate = normalizeDateSearchValue(val);
            if (normalizedDate) searchParams[f.key] = normalizedDate;
          } else {
            const trimmed = (val || '').trim();
            if (trimmed) searchParams[f.key] = trimmed;
          }
        });
        requestParams = {
          emr_integration_credential_id: credentialId,
          search_params: searchParams,
          limit,
        };
      } else {
        const queryVal = (requestFieldValues.query || '').trim();
        requestParams = {
          emr_integration_credential_id: credentialId,
          query: queryVal,
          limit,
        };
      }

      if (location) {
        requestParams.location = location;
      }

      axios.get('/api/v1/emr_patients', { params: requestParams })
        .then((response) => {
          if (cancellationToken.cancelled) return;
          setResults(response.data?.patients || []);
          setSearchWarnings(response.data?.search_warnings || []);
          setHasMore(response.data?.has_more === true);
          setRequestId(response.data?.request_id || null);
          setAcceptedResponseSequence((sequence) => sequence + 1);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancellationToken.cancelled) return;
          setError(buildErrorMessage(err));
          setIsLoading(false);
        });
    }, effectiveDebounce);

    return () => {
      clearTimeout(timerId);
      cancellationToken.cancelled = true;
    };
  }, [
    requestFieldValuesKey,
    searchRequestId,
    requestCanSearch,
    credentialId,
    isStructuredSearch,
    searchFields,
    limit,
    effectiveDebounce,
    location,
  ]);

  return useMemo(() => ({
    fieldValues,
    setFieldValue,
    results,
    searchWarnings,
    hasMore,
    acceptedResponseSequence,
    requestId,
    isLoading,
    error,
    canSearch,
    search,
    reset,
  }), [
    fieldValues,
    setFieldValue,
    results,
    searchWarnings,
    hasMore,
    acceptedResponseSequence,
    requestId,
    isLoading,
    error,
    canSearch,
    search,
    reset,
  ]);
};

export default useEmrPatientSearch;
